import React from 'react'

import { fmtProb } from '@/utils/format'

/**
 * Normalised "scenario card" shape used across the dashboard. Backend
 * payloads come in two flavours:
 *
 * 1. **Bull / Base / Bear** (legacy memo + reports): keyed by name, with
 *    `summary | description | narrative | thesis` and `catalysts |
 *    triggers | key_drivers` arrays.
 * 2. **Scenario frame** (current memo artifact): each entry has `case`,
 *    `label`, `title`, `thesis`, `what_must_be_true`,
 *    `confirming_signals`, `invalidating_signals`, `action`.
 *
 * Use {@link normalizeScenarioFrame} on either shape to get a single
 * canonical structure that {@link ScenarioFrameCard} renders.
 */
export type ScenarioFrame = {
  /** Canonical key (bull/base/bear) when known; otherwise `null`. Used to
   *  pick the polarity tone of the card. */
  caseKey: 'bull' | 'base' | 'bear' | null
  /** Compact label, e.g. "BULL" / "BASE" / "BEAR" or a custom case label
   *  ("Constructive earnings"). */
  label: string
  /** Optional descriptive title (longer). */
  title?: string | null
  /** Probability in 0..1 if available. */
  probability?: number | null
  /** Customer-facing source label for the probability, e.g. "Case forecast". */
  probabilityLabel?: string | null
  /** Machine-readable basis, e.g. `case_set_forecast` or `outlook_synthesis`. */
  probabilityBasis?: string | null
  /** Short provenance note for the probability. */
  probabilityNote?: string | null
  /** Stable trace id for a dedicated case-set forecast, when available. */
  forecastTraceId?: string | null
  /** Forecast question ids used as source drivers for the case frame. */
  sourceQuestionIds: Array<string | number>
  /** Forecast run ids used as source drivers, when the backend can provide them. */
  sourceForecastRunIds: Array<string | number>
  /** Source report artifact id for seeded or synthesized case frames. */
  sourceReportId?: string | number | null
  sourceReportIds: Array<string | number>
  promptUsageIds: Array<string | number>
  /** Prose thesis / summary. */
  thesis?: string | null
  /** Pre-conditions that would have to hold for the scenario to play. */
  whatMustBeTrue: string[]
  /** Signals that would confirm the scenario. */
  confirmingSignals: string[]
  /** Signals that would invalidate the scenario. */
  invalidatingSignals: string[]
  /** Short PM action hint, e.g. "Watch the FQ4 print". */
  action?: string | null
}

const TONE: Record<'bull' | 'base' | 'bear' | 'other', string> = {
  bull: 'var(--ok, #16a34a)',
  base: 'var(--accent, #6366f1)',
  bear: 'var(--bad, #dc2626)',
  other: 'var(--accent, #6366f1)',
}

function asItems(value: any): string[] {
  if (!value) return []
  if (typeof value === 'string') return value.trim() ? [value.trim()] : []
  if (Array.isArray(value)) {
    return value
      .map((item) => {
        if (typeof item === 'string') return item.trim()
        if (item && typeof item === 'object') {
          return String(
            item.text || item.title || item.label || item.summary || item.signal || '',
          ).trim()
        }
        return ''
      })
      .filter(Boolean)
  }
  return []
}

function asIdItems(value: any): Array<string | number> {
  if (!Array.isArray(value)) return []
  return value
    .map((item) => {
      if (typeof item === 'number' && Number.isFinite(item)) return item
      const text = String(item || '').trim()
      return text || null
    })
    .filter((item): item is string | number => item !== null)
}

function detectCaseKey(value?: string | null): ScenarioFrame['caseKey'] {
  const v = String(value || '').toLowerCase()
  if (v === 'bull' || v === 'base' || v === 'bear') return v
  return null
}

function shortTraceId(value?: string | null): string | null {
  const text = String(value || '').trim()
  if (!text) return null
  return text.length > 18 ? text.slice(-16) : text
}

/**
 * Normalises any of the supported scenario payload shapes into a single
 * {@link ScenarioFrame}.
 *
 * Pass `keyHint` when the raw object is keyed by name (e.g. the
 * `scenarios.bull` payload) so the resulting frame still carries the
 * polarity, even if the raw object doesn't include a `case` field.
 */
export function normalizeScenarioFrame(raw: any, keyHint?: 'bull' | 'base' | 'bear'): ScenarioFrame | null {
  if (!raw || typeof raw !== 'object') return null
  const caseKey =
    detectCaseKey(raw.case || raw.scenario || raw.kind) ?? keyHint ?? null
  const label =
    raw.label
    || raw.title
    || (keyHint ? keyHint.toUpperCase() : null)
    || 'Scenario'
  const thesis = raw.thesis || raw.summary || raw.description || raw.narrative || null
  return {
    caseKey,
    label: String(label),
    title: raw.title || null,
    probability: typeof raw.probability === 'number' ? raw.probability : null,
    probabilityLabel: raw.probability_label || raw.probability_basis || null,
    probabilityBasis: raw.probability_basis || null,
    probabilityNote: raw.probability_note || raw.provenance_note || null,
    forecastTraceId: raw.forecast_trace_id || raw.trace_id || null,
    sourceQuestionIds: asIdItems(raw.source_question_ids || raw.linked_question_ids),
    sourceForecastRunIds: asIdItems(raw.source_forecast_run_ids),
    sourceReportId: raw.source_report_id || null,
    sourceReportIds: asIdItems(raw.source_report_ids || raw.source_artifact_ids),
    promptUsageIds: asIdItems(raw.prompt_usage_ids),
    thesis: thesis ? String(thesis) : null,
    whatMustBeTrue: asItems(raw.what_must_be_true || raw.preconditions),
    confirmingSignals: asItems(
      raw.confirming_signals || raw.catalysts || raw.triggers || raw.key_drivers,
    ),
    invalidatingSignals: asItems(raw.invalidating_signals || raw.invalidators),
    action: raw.action ? String(raw.action) : null,
  }
}

type ScenarioFrameCardProps = {
  frame: ScenarioFrame
  /** Compact variant: lighter padding and smaller typography for use in
   *  grids of three (bull/base/bear). */
  compact?: boolean
}

function MiniList({ title, items }: { title: string; items: string[] }) {
  if (!items.length) return null
  return (
    <div style={{ marginTop: 10 }}>
      <div
        style={{
          fontSize: 10,
          fontWeight: 800,
          textTransform: 'uppercase',
          letterSpacing: 0.5,
          color: 'var(--muted)',
        }}
      >
        {title}
      </div>
      <ul style={{ margin: '4px 0 0 18px', padding: 0, fontSize: 12, lineHeight: 1.45 }}>
        {items.slice(0, 4).map((item, idx) => (
          <li key={idx}>{item}</li>
        ))}
      </ul>
    </div>
  )
}

/**
 * Canonical scenario card. One look across CompanyDetail's memo and
 * details views, future workspace renderings, and the shareable reports.
 */
export default function ScenarioFrameCard({ frame, compact = false }: ScenarioFrameCardProps) {
  const tone = TONE[frame.caseKey ?? 'other']
  const padding = compact ? 12 : 16
  const radius = compact ? 14 : 18
  const sourceForecastCount = frame.sourceForecastRunIds.length || frame.sourceQuestionIds.length
  const traceLabel = shortTraceId(frame.forecastTraceId)
  const provenance = [
    sourceForecastCount ? `Source forecasts: ${sourceForecastCount}` : null,
    traceLabel ? `Trace: ${traceLabel}` : null,
  ].filter(Boolean)
  return (
    <div
      style={{
        padding,
        borderRadius: radius,
        border: '1px solid var(--border)',
        background: 'var(--card)',
      }}
    >
      <div className="row" style={{ justifyContent: 'space-between', gap: 12, alignItems: 'flex-start' }}>
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              fontSize: 11,
              fontWeight: 850,
              textTransform: 'uppercase',
              letterSpacing: 0.6,
              color: tone,
            }}
          >
            {frame.label}
          </div>
          {frame.title && frame.title !== frame.label ? (
            <div style={{ marginTop: 4, fontSize: compact ? 14 : 17, fontWeight: 800, lineHeight: 1.2 }}>
              {frame.title}
            </div>
          ) : null}
        </div>
        <div style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
          <div style={{ fontSize: compact ? 18 : 21, fontWeight: 850, color: tone }}>
            {typeof frame.probability === 'number' ? fmtProb(frame.probability) : '—'}
          </div>
          {frame.probabilityLabel ? (
            <div
              title={frame.probabilityNote || undefined}
              style={{ marginTop: 2, fontSize: 10, fontWeight: 750, color: 'var(--muted)' }}
            >
              {frame.probabilityLabel}
            </div>
          ) : null}
          {provenance.length ? (
            <div
              title={frame.forecastTraceId || frame.probabilityNote || undefined}
              style={{ marginTop: 4, fontSize: 10, fontWeight: 650, color: 'var(--muted)' }}
            >
              {provenance.join(' · ')}
            </div>
          ) : null}
        </div>
      </div>
      {frame.thesis ? (
        <div style={{ marginTop: 10, fontSize: compact ? 12 : 13, lineHeight: 1.55 }}>{frame.thesis}</div>
      ) : null}
      <MiniList title="Must be true" items={frame.whatMustBeTrue} />
      <MiniList title="Confirming signals" items={frame.confirmingSignals} />
      <MiniList title="Invalidating signals" items={frame.invalidatingSignals} />
      {frame.action ? (
        <div style={{ marginTop: 10, fontSize: 12, lineHeight: 1.5 }}>
          <strong>Action:</strong> {frame.action}
        </div>
      ) : null}
    </div>
  )
}
